import { useModal } from '@bitstopco/bitstop-theme';
import { Box, Button, Card, Stack, Typography } from '@mui/material';

import Chip from '@/components/ui/Chip';

import { useDispenseAssets } from '@/hooks/useDispenseAssets';
import type { CassetteView } from '@/types/generated/athena';

type Props = {
  atm_id: number;
  cassette: CassetteView;
};

const badgeStyle = { fontWeight: 'bold' };

const CassetteInventoryCard = ({ cassette, atm_id }: Props) => {
  const { show } = useModal();
  const { billsMap } = useDispenseAssets();
  const { cassette_index, bill_note_id, count, low_quantity_threshold } = cassette;
  const isLow = bill_note_id ? (count ?? 0) <= (low_quantity_threshold ?? 0) : false;
  const isEmpty = !bill_note_id;

  const border = isLow && !isEmpty ? '2px solid #ff0000' : undefined;

  const handleThresholdChange = () =>
    show('edit-inventory-threshold', { atmId: atm_id, index: cassette.cassette_index });

  return (
    <Card sx={{ height: '100%', border }}>
      <Stack p={2} height="100%">
        <Stack mb={2} direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="overline" sx={{ borderLeft: '4px solid red', pl: 2 }}>
            Cassette {cassette_index}
          </Typography>
          {isEmpty && <Chip color="primary" size="small" label="Empty" sx={badgeStyle} />}
          {isLow && !isEmpty && <Chip color="error" size="small" label="Low" sx={badgeStyle} />}
          {!isEmpty && !isLow && <Chip color="success" size="small" label="Ok" sx={badgeStyle} />}
        </Stack>
        {isEmpty && (
          <Stack
            justifyContent="center"
            bgcolor="neutral.50"
            textAlign="center"
            borderRadius={1}
            flex={1}
            p={2}
          >
            <Typography variant="caption" color="text.secondary">
              Current stock
            </Typography>
            <Typography variant="overline" lineHeight={2} fontSize={14}>
              Empty
            </Typography>
          </Stack>
        )}
        {!isEmpty && (
          <Stack gap={2}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Asset
              </Typography>
              <Typography variant="body1">{billsMap[bill_note_id]?.at(0) ?? '-'}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Denomination
              </Typography>
              <Typography variant="body1">{billsMap[bill_note_id]?.at(1) ?? '-'}</Typography>
            </Box>
            <Box bgcolor="neutral.50" p={2} borderRadius={1}>
              <Typography variant="body2" color="text.secondary">
                Current stock
              </Typography>
              <Typography variant="body1">{count}</Typography>
            </Box>
          </Stack>
        )}
        <Button
          size="small"
          color="info"
          variant="text"
          sx={{ mt: 2 }}
          onClick={handleThresholdChange}
        >
          Threshold {low_quantity_threshold} items
        </Button>
      </Stack>
    </Card>
  );
};

export default CassetteInventoryCard;
